import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import Link from '@alltrails/denali/components/Link';
import { Key } from 'react';
import { StaticImageData } from 'next/image';
import lizImg from '../../assets/images/leadership/liz.avif';
import juliaImg from '../../assets/images/leadership/julia.avif';
import carlyImg from '../../assets/images/leadership/carly.avif';
import mikeImg from '../../assets/images/leadership/mike.avif';
import jamesImg from '../../assets/images/leadership/james.avif';
import ivanImg from '../../assets/images/leadership/ivan.avif';
import LeadershipCard from './LeadershipCard';
import styles from './styles/styles.module.scss';

type LeaderType = {
  bio: React.ReactNode;
  name: React.ReactNode;
  photo: StaticImageData;
  pronouns: React.ReactNode;
  title: React.ReactNode;
  contact?: string;
};

const leaders: LeaderType[] = [
  {
    bio: (
      <>
        <p>
          <FormattedMessage defaultMessage="Liz Hamren is the CEO of AllTrails. She has extensive experience in building consumer technology products that enrich people's lives and in launching industry-defining products and subscription services that delight customers." />
        </p>
        <p>
          <FormattedMessage defaultMessage="Prior to AllTrails, Liz was the CEO of Ring (an Amazon company) where she oversaw Ring, Blink, Amazon Key, and Amazon Sidewalk leading Amazon's global home and business smart security offerings. Prior to Ring, she served as COO of Discord, and Corporate Vice President at Microsoft, leading product and engineering for Xbox consoles and Game Pass." />
        </p>
        <p>
          <FormattedMessage
            defaultMessage="She has also held executive roles at Meta where she led global sales and marketing for Oculus as well as at Dropcam and Plantronics. Liz holds a BSE in Civil Engineering and Operations Research from Princeton University, and an MBA from Harvard Business School. She serves on the Board of Directors for Hasbro, Inc. and LegalZoom, Inc. She lives with her family in Seattle, WA, and can be found exploring all the Pacific Northwest has to offer. Her all-time favorite trail is <a>Zuma Ridge</a> in the Santa Monica mountains."
            values={{
              a: chunks => (
                <Link className={styles.link} href="/trail/us/california/zuma-ridge-trail" target="_blank">
                  {chunks}
                </Link>
              )
            }}
          />
        </p>
      </>
    ),
    name: <FormattedMessage defaultMessage="Liz Hamren" />,
    photo: lizImg,
    pronouns: <FormattedMessage defaultMessage="(she/her)" />,
    title: <FormattedMessage defaultMessage="Chief Executive Officer" />
  },
  {
    bio: (
      <>
        <p>
          <FormattedMessage defaultMessage="As Chief Financial Officer, Mike Doyle leads business insights, analysis, and financial strategy to deliver customer value; additionally, he drives cross functional execution in support of the company's growth and profitability goals. Mike has served as CFO in public and private consumer technology companies in Asia, Latin America, and the U.S. including Nextdoor, Despegar, and Expedia. He started his career in the investment banking division of Morgan Stanley and the private equity group of GIC." />
        </p>
        <p>
          <FormattedMessage
            defaultMessage="Mike holds a bachelor's degree in finance from Southern Methodist University and an MBA from Harvard Business School. He lives in San Francisco with his wife and their three children. Mike loves hiking in Joshua Tree National Park, but his all-time favorite trail is the <a>Perito Moreno Glacier Walkways hike</a> in Los Glaciares National Park in Argentina."
            values={{
              a: chunks => (
                <Link className={styles.link} href="/trail/argentina/santa-cruz/sendero-glaciar-perito-moreno" target="_blank">
                  {chunks}
                </Link>
              )
            }}
          />
        </p>
      </>
    ),
    name: <FormattedMessage defaultMessage="Mike Doyle" />,
    photo: mikeImg,
    pronouns: <FormattedMessage defaultMessage="(he/him)" />,
    title: <FormattedMessage defaultMessage="Chief Financial Officer" />
  },
  {
    bio: (
      <>
        <p>
          <FormattedMessage defaultMessage="As Chief Technology Officer, James leads the engineering teams responsible for bringing AllTrails to life across iOS, Android, and Web. James is driven by the opportunity to help millions of people explore the natural world through innovative digital experiences. Previously, at GoPro, he helped shape the cloud engineering team and grow the GoPro Plus subscription service. His career spans a range of impactful contributions to both enterprise and consumer software companies, from grassroots startups to large-scale organizations." />
        </p>
        <p>
          <FormattedMessage
            defaultMessage="James earned his bachelor's degree in Computer Science and Management Information Systems from The University of Alabama. While there, he was an active member of the university's Crew and Kayak Club. As a San Francisco resident, James loves exploring the Bay Area and snowboarding in Tahoe. In the summer, you may find him exploring <a>Ropi Lake</a> in the Desolation Wilderness with family and friends."
            values={{
              a: chunks => (
                <Link className={styles.link} href="/trail/us/california/ropi-lake--2" target="_blank">
                  {chunks}
                </Link>
              )
            }}
          />
        </p>
      </>
    ),
    name: <FormattedMessage defaultMessage="James Graham" />,
    photo: jamesImg,
    pronouns: <FormattedMessage defaultMessage="(he/him)" />,
    title: <FormattedMessage defaultMessage="Chief Technology Officer" />
  },
  {
    bio: (
      <>
        <p>
          <FormattedMessage defaultMessage="As Chief Product Officer, Ivan Selin leads the AllTrails product, design, research, and operations teams to deliver delightful experiences that our users love. His extensive career includes leadership positions in product management, product marketing, and growth with consumer tech companies Airbnb, Uber, Apple, and Motorola. With a love for travelling, hiking, and sports, Ivan combines user-centric strategies with his outdoor experience to drive the pioneering AllTrails product vision." />
        </p>
        <p>
          <FormattedMessage
            defaultMessage="Ivan earned master's degrees in Computer Science & Engineering and Management from St. Petersburg Polytechnic University and an MBA from the University of Chicago Booth School of Business. He lives in the San Francisco Bay Area with his wife and two kids, and enjoys hiking with his family both locally and across the world. Ivan's favourite trail is <a>Showers Lake</a> located in South Lake Tahoe, California."
            values={{
              a: chunks => (
                <Link className={styles.link} href="/trail/us/california/showers-lake" target="_blank">
                  {chunks}
                </Link>
              )
            }}
          />
        </p>
      </>
    ),
    name: <FormattedMessage defaultMessage="Ivan Selin" />,
    photo: ivanImg,
    pronouns: <FormattedMessage defaultMessage="(he/him)" />,
    title: <FormattedMessage defaultMessage="Chief Product Officer" />
  },
  {
    bio: (
      <>
        <p>
          <FormattedMessage defaultMessage="As Chief Marketing Officer, Carly leads the company's efforts to help millions of people around the world discover AllTrails and be inspired to get outside. Carly works to build a brand that people love, bring new features to market, and expand AllTrails' marketing capabilities to reach new people — all so that more people can experience the goodness of the outdoors. Carly has a broad range of consumer marketing experience and previously led marketing at Minted and e-commerce marketing at Levi Strauss." />
        </p>
        <p>
          <FormattedMessage
            defaultMessage="Carly graduated from Bowdoin College, where she explored the outdoors of Maine as a Bowdoin Outing Club trip leader. She earned her MBA from Harvard Business School. Carly serves on the Outdoor Recreation Roundtable Business Council and Fast Company Impact Council. She loves hiking and mountain biking near her home in Marin; the <a>Tennessee Valley to Muir Beach trail</a> is one of her all-time favorites."
            values={{
              a: chunks => (
                <Link className={styles.link} href="/trail/us/california/tennessee-valley-to-muir-beach-via-coastal-trail" target="_blank">
                  {chunks}
                </Link>
              )
            }}
          />
        </p>
      </>
    ),
    name: <FormattedMessage defaultMessage="Carly Smith" />,
    photo: carlyImg,
    pronouns: <FormattedMessage defaultMessage="(she/her)" />,
    title: <FormattedMessage defaultMessage="Chief Marketing Officer" />
  },
  {
    bio: (
      <>
        <p>
          <FormattedMessage defaultMessage="Julia Riessen leads AllTrails' strategies to attract, engage, and support a talented and passionate team. She oversees People Operations, Office Operations, and Talent Acquisition, driving the development of people programs and a company culture that reflect AllTrails' values and mission. Julia has strong leadership experience in human resources, with a career that includes a decade at Lyft where she played a key role in managing the company's rapid growth and transformation. Her background also includes roles at Opower and Hewlett Packard. Julia earned a Bachelor of Arts in Economics with a minor in Psychology from Scripps College." />
        </p>
        <p>
          <FormattedMessage
            defaultMessage="Julia has a lifelong love of the outdoors–particularly hiking in Tahoe and Marin–and enjoys sharing those experiences with her husband, two children, and the family dog. She resides in Oakland, California and one of her all-time favorite trails is the <a>Shirley Canyon Trail</a> in Olympic Valley, California."
            values={{
              a: chunks => (
                <Link className={styles.link} href="/trail/us/california/shirley-canyon-cascades-trail" target="_blank">
                  {chunks}
                </Link>
              )
            }}
          />
        </p>
      </>
    ),
    name: <FormattedMessage defaultMessage="Julia Riessen" />,
    photo: juliaImg,
    pronouns: <FormattedMessage defaultMessage="(she/her)" />,
    title: <FormattedMessage defaultMessage="Vice President of People" />
  }
];

// Desktop: https://www.figma.com/file/XrlDM2ZdWXmlnl7ZXUicGn/Rebrand%E2%80%93Press%2FAbout%2F404?node-id=1701%3A10812&t=cQ6ofstqefHmuy2U-4
// Mobile: https://www.figma.com/file/XrlDM2ZdWXmlnl7ZXUicGn/Rebrand%E2%80%93Press%2FAbout%2F404?node-id=2506%3A11457&t=aadNlnIKQJkCzSxu-4
export default function Leadership() {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography variant="heading700">
          <FormattedMessage defaultMessage="Our leadership team" />
        </Typography>
        <div className={styles.leaders}>
          {leaders.map((leader, index) => (
            <LeadershipCard leader={leader} key={index as Key} />
          ))}
        </div>
      </div>
    </div>
  );
}

export type Leader = (typeof leaders)[number];
